import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { Button, Divider, Modal, notification, NumberInput, Trans } from '@/components'
import { InsufficientQuotaView } from '@/components/InsufficientQuotaView'
import { errorMsg } from '@/consts/errorMsg'
import { INSTANCE_SUBSCRIPTION_ENABLED } from '@/consts/features'
import { MachineType, PaymentType } from '@/consts/machine'
import {
  BILLING_METHODS,
  type BillingMethod,
  DURATION,
  type Duration,
  ItemPriceType,
} from '@/consts/order'
import { hasPrivateRegionDomain } from '@/consts/privateRegion'
import { getBonusStat } from '@/models/credit'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { useUserStore } from '@/stores/user'
import { showError } from '@/utils/error'
import { formatPrice } from '@/utils/formatter'

import { AutoRenewalSection } from './components/AutoRenewalSection'
import { BillingMethods } from './components/BillingMethods'
import { BindNATModal } from './components/BindNATModal'
import { BindPublicIPSection } from './components/BindPublicIPSection'
import { ConfirmationModal } from './components/ConfirmationModal'
import { ExpirationBehavior } from './components/ExpirationBehavior'
import {
  type ExtraResourceChangeReason,
  ExtraSpecificationSection,
} from './components/ExtraSpecificationSection'
import { InsufficientRechargeCreditsModal } from './components/InsufficientRechargeCreditsModal'
import { InsufficientStorageModal } from './components/InsufficientStorageModal'
import { MachineSelector } from './components/machine-selector'
import { MountDriveModal } from './components/MountDriveModal'
import { MountDriveSection, PERSONAL_SECTION_CONFIG } from './components/MountDriveSection'
import { PurchaseForSection, type PurchaseValue } from './components/PurchaseForSection'
import { SelectMachineTooltip } from './components/SelectMachineTooltip'
import { SubscriptionCreditAlert } from './components/SubscriptionCreditAlert'
import { OrderSummary } from './components/summary'
import { useMachinePrice } from './hooks/useMachinePrice'
import { useMountConfig } from './hooks/useMountConfig'
import { useVolumeData } from './hooks/useVolumeData'
import type { CreateInstanceBoundNATInfo } from './types'
import {
  buildCreateInstancePayload,
  findSubscriptionPlan,
  getSubscriptionPriceCent,
  getSubscriptionQuantity,
  hasSufficientRechargeCredits,
  supportsSubscription,
} from './utils'
import {
  EMPTY_EXTRA_RESOURCE_CONFIG,
  ExtraResourceConfig,
  getMinDiskGB,
  getMinimumInstanceCountForBootSource,
  getMinResource,
  getRequiredExtraResourceConfig,
  getResourceRequirementStatus,
  reconcileAutoExtraResourceConfig,
} from './utils/extraStorage'

// Specific images require a minimum number of instances to function correctly
// Maps image resID to the minimum required instance count
const IMAGE_MINIMUM_INSTANCE_COUNT: Record<string, number> = {
  'img-5yp6v8l8': 4,
  'img-y4lvnxqw': 2,
}

export function CreateInstancePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userStore = useUserStore()

  const [type, setType] = useState(MachineType.GPU)
  const period = PaymentType.Hourly
  const [machine, setMachine] = useState<Machine>()
  const [count, setCount] = useState(1)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isStorageWarningOpen, setIsStorageWarningOpen] = useState(false)
  const [isCreatingIns, setIsCreatingIns] = useState(false)
  const [checkedImage, setCheckedImage] = useState<Image>()
  const [checkedSnap, setCheckedSnap] = useState<Snapshot>()
  const [extraResourceConfig, setExtraResourceConfig] = useState<ExtraResourceConfig>(
    EMPTY_EXTRA_RESOURCE_CONFIG,
  )
  const autoExtraResourceConfigRef = useRef<ExtraResourceConfig>(EMPTY_EXTRA_RESOURCE_CONFIG)
  const isExtraResourceUserManagedRef = useRef(false)
  const handleExtraResourceChange = useCallback(
    (value: ExtraResourceConfig, reason: ExtraResourceChangeReason) => {
      const isUserManaged = reason === 'user'

      isExtraResourceUserManagedRef.current = isUserManaged
      autoExtraResourceConfigRef.current = isUserManaged ? EMPTY_EXTRA_RESOURCE_CONFIG : value
      setExtraResourceConfig(value)
    },
    [],
  )
  const [billingMethod, setBillingMethod] = useState<BillingMethod>(BILLING_METHODS.PAY_AS_YOU_GO)
  const [duration, setDuration] = useState<Duration>(DURATION.DAILY)
  const [subscriptionTotalDays, setSubscriptionTotalDays] = useState<number | undefined>(1)
  const [purchaseFor, setPurchaseFor] = useState<PurchaseValue>(1)
  const [autoRenewal, setAutoRenewal] = useState(false)
  const [expirationBehavior, setExpirationBehavior] = useState(false)
  const [insufficientRechargeOpen, setInsufficientRechargeOpen] = useState(false)
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const isSubscription = billingMethod === BILLING_METHODS.SUBSCRIPTION

  // Mount Drive
  const [mountModalOpen, setMountModalOpen] = useState(false)
  const { volumes: cloudVolumes, loading: volumesLoading } = useVolumeData(machine?.domains)
  const { mountVolume, mountPath, readWriteMode, updateMountConfig, resetMountConfig } =
    useMountConfig()

  const [boundNAT, setBoundNAT] = useState<CreateInstanceBoundNATInfo | undefined>(undefined)
  const [bindNATModalOpen, setBindNATModalOpen] = useState(false)

  const subscriptionPlan = useMemo(
    () => (isSubscription ? findSubscriptionPlan(machine?.items, duration) : undefined),
    [duration, isSubscription, machine],
  )
  const subscriptionPriceCent = useMemo(
    () =>
      isSubscription
        ? getSubscriptionPriceCent({
            plan: subscriptionPlan,
            duration,
            totalDays: subscriptionTotalDays,
            count,
            priceType: ItemPriceType.Original,
          })
        : undefined,
    [count, duration, isSubscription, subscriptionPlan, subscriptionTotalDays],
  )
  const rechargeCreditCent = getBonusStat(userStore.account)?.rechargeBonus.moneyCent
  const rechargeCreditDataAvailable =
    rechargeCreditCent !== undefined &&
    Number.isFinite(rechargeCreditCent) &&
    rechargeCreditCent >= 0
  const subscriptionCheckoutDataAvailable =
    !isSubscription ||
    (subscriptionPlan !== undefined &&
      subscriptionPriceCent !== undefined &&
      subscriptionPriceCent > 0 &&
      rechargeCreditDataAvailable &&
      machine?.domains?.[0] !== undefined)

  const {
    data: priceData,
    loading: priceLoading,
    priceAvailable,
    extraStorageGB,
    availableExtraStorageGB,
    extraCpuPriceMillicent,
    extraRamPriceMillicent,
    extraStoragePriceMillicent,
    extraResourceDesc,
  } = useMachinePrice({
    machine,
    count,
    checkedImage,
    checkedSnap,
    extraResourceConfig,
  })

  const selectedBootSource = checkedSnap ?? checkedImage
  const notifyInstanceCountAdjustment = useCallback(
    (nextCount: number) => {
      const sourceType = checkedSnap ? 'snapshot' : 'image'
      const sourceTypeLabel = t(`create.bootSourceType.${sourceType}`)

      notification.info(
        t('create.instanceCountAdjustedForBootSource', {
          count: nextCount,
          sourceType: sourceTypeLabel,
        }),
      )
    },
    [checkedSnap, t],
  )
  const clearBootSourceIfIncompatible = useCallback(
    (
      nextCount: number,
      supportsExtraResources: boolean,
      reason: 'subscription' | 'configuration',
    ) => {
      if (!selectedBootSource) return false

      const minimumInstanceCount = getMinimumInstanceCountForBootSource({
        machine,
        count: nextCount,
        source: selectedBootSource,
        supportsExtraResources,
      })
      if (minimumInstanceCount !== undefined) {
        if (minimumInstanceCount > nextCount) {
          setCount(minimumInstanceCount)
          notifyInstanceCountAdjustment(minimumInstanceCount)
        }

        return false
      }

      const sourceType = checkedSnap ? 'snapshot' : 'image'
      const sourceTypeLabel = t(`create.bootSourceType.${sourceType}`)

      setCheckedImage(undefined)
      setCheckedSnap(undefined)
      notification.warning(
        t(
          reason === 'subscription'
            ? 'create.bootSourceClearedForSubscription'
            : 'create.bootSourceClearedForConfiguration',
          { sourceType: sourceTypeLabel },
        ),
      )

      return true
    },
    [checkedSnap, machine, notifyInstanceCountAdjustment, selectedBootSource, t],
  )
  const minimumResourceInstanceCount = useMemo(
    () =>
      selectedBootSource
        ? getMinimumInstanceCountForBootSource({
            machine,
            count: machine?.unitStep ?? 1,
            source: selectedBootSource,
            supportsExtraResources: !isSubscription,
          })
        : undefined,
    [isSubscription, machine, selectedBootSource],
  )
  const minimumBootSourceInstanceCount = Math.max(
    machine?.unitStep ?? 1,
    minimumResourceInstanceCount ?? 1,
    checkedImage ? (IMAGE_MINIMUM_INSTANCE_COUNT[checkedImage.resID] ?? 1) : 1,
  )
  const selectedMinDiskGB = getMinDiskGB(selectedBootSource)
  const baseStorageGB = (machine?.unitSpec?.diskGB ?? 0) * count
  const minimumRequiredExtraStorageGB = Math.max(selectedMinDiskGB - baseStorageGB, 0)
  const requiredExtraResourceConfig = useMemo(
    () => getRequiredExtraResourceConfig(machine, count, selectedBootSource),
    [machine, count, selectedBootSource],
  )
  const requiredExtraStorageGB = requiredExtraResourceConfig.diskGB ?? 0
  const resourceRequirementStatus = getResourceRequirementStatus({
    machine,
    count,
    source: selectedBootSource,
    extraResourceConfig,
  })
  const storageInsufficient = resourceRequirementStatus.diskInsufficient

  useEffect(() => {
    setCount(machine?.unitStep ?? 1)
    setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
    autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
    isExtraResourceUserManagedRef.current = false
    resetMountConfig()
  }, [machine, resetMountConfig])

  useEffect(() => {
    if (isSubscription) {
      setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
      autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
      isExtraResourceUserManagedRef.current = false
      return
    }

    if (!machine) {
      setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
      autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
      isExtraResourceUserManagedRef.current = false
      return
    }

    setExtraResourceConfig((prev) => {
      const { config, trackedAutoConfig } = reconcileAutoExtraResourceConfig(
        machine,
        prev,
        requiredExtraResourceConfig,
        autoExtraResourceConfigRef.current,
        isExtraResourceUserManagedRef.current,
      )

      autoExtraResourceConfigRef.current = trackedAutoConfig

      return config
    })
  }, [isSubscription, machine, requiredExtraResourceConfig])

  useEffect(() => {
    if (
      !selectedBootSource ||
      minimumResourceInstanceCount === undefined ||
      count >= minimumBootSourceInstanceCount ||
      minimumBootSourceInstanceCount > (machine?.maxAvailUnits ?? 0)
    ) {
      return
    }

    setCount(minimumBootSourceInstanceCount)
    notifyInstanceCountAdjustment(minimumBootSourceInstanceCount)
  }, [
    count,
    machine,
    minimumBootSourceInstanceCount,
    minimumResourceInstanceCount,
    notifyInstanceCountAdjustment,
    selectedBootSource,
  ])
  const canCreate = useMemo(() => {
    return Boolean(machine && (checkedImage || checkedSnap) && subscriptionCheckoutDataAvailable)
  }, [checkedImage, checkedSnap, machine, subscriptionCheckoutDataAvailable])

  const onConfirm = useCallback(
    async (name: string) => {
      if (!machine) return
      setIsCreatingIns(true)

      try {
        const createRequest = buildCreateInstancePayload({
          machine,
          count,
          checkedImage,
          checkedSnap,
          mountVolume,
          mountPath,
          readWriteMode,
          boundNAT: isSubscription ? undefined : boundNAT,
          remark: name,
          extraResourceDesc: isSubscription ? undefined : extraResourceDesc,
        })

        if (!isSubscription) {
          const res = await API.createInstance(createRequest)
          if (res.code === 0) {
            setIsConfirmModalOpen(false)
            navigate('/instances')
          } else if (res.code === 194) {
            showError(errorMsg[194], '194')
          }
          return
        }

        const quantity = getSubscriptionQuantity(duration, subscriptionTotalDays)
        if (
          !subscriptionPlan ||
          !subscriptionPriceCent ||
          rechargeCreditCent === undefined ||
          quantity <= 0
        ) {
          return
        }

        if (!hasSufficientRechargeCredits(subscriptionPriceCent, rechargeCreditCent)) {
          setIsConfirmModalOpen(false)
          setInsufficientRechargeOpen(true)
          return
        }

        const shouldConvertToPayAsYouGoAfterExpiration = expirationBehavior
        const domain = machine.domains?.[0]
        if (domain === undefined) return

        const res = await API.purchaseInstanceSubscription({
          domain,
          info: JSON.stringify(createRequest),
          res_id: subscriptionPlan.resID,
          qty: quantity,
          auto_renewal: autoRenewal,
          order_to_billing: shouldConvertToPayAsYouGoAfterExpiration,
        })

        if (res.code === 0) {
          setIsConfirmModalOpen(false)
          navigate('/instances')
        }
      } finally {
        setIsCreatingIns(false)
      }
    },
    [
      machine,
      count,
      checkedImage,
      checkedSnap,
      mountVolume,
      mountPath,
      readWriteMode,
      boundNAT,
      extraResourceDesc,
      isSubscription,
      navigate,
      duration,
      subscriptionTotalDays,
      subscriptionPlan,
      subscriptionPriceCent,
      rechargeCreditCent,
      expirationBehavior,
      autoRenewal,
    ],
  )

  useEffect(() => {
    setMachine(undefined)
    setCheckedImage(undefined)
    setCheckedSnap(undefined)
    setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
    autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
    isExtraResourceUserManagedRef.current = false
    resetMountConfig()
  }, [type, resetMountConfig])

  return (
    <div
      className="h-full relative"
      data-ai-component="create-instance-page"
      data-ai-state={
        canCreate ? 'ready-for-checkout' : machine ? 'needs-image-or-snapshot' : 'needs-machine'
      }
    >
      <div
        className="card !px-0 bg-panel-white h-full overflow-y-auto"
        data-ai-component="create-instance-workflow"
      >
        <div className="flex h-[60px] items-start justify-between px-8">
          <div className="h6">
            <Trans i18nKey="create.createNew">Create New</Trans>
          </div>
        </div>

        <Divider className="px-8" />

        <div className="px-8">
          {INSTANCE_SUBSCRIPTION_ENABLED ? (
            <div className="mb-4">
              <BillingMethods
                billingMethod={billingMethod}
                duration={duration}
                onBillingMethodChange={(nextMethod) => {
                  setBillingMethod(nextMethod)

                  if (nextMethod === BILLING_METHODS.PAY_AS_YOU_GO) {
                    setSubscriptionTotalDays(undefined)
                    return
                  }

                  setBoundNAT(undefined)
                  setBindNATModalOpen(false)
                  setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
                  autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
                  isExtraResourceUserManagedRef.current = false
                  setPurchaseFor(1)
                  setDuration(DURATION.DAILY)
                  setSubscriptionTotalDays(1)

                  if (!supportsSubscription(machine, DURATION.DAILY)) {
                    setMachine(undefined)
                    setCheckedImage(undefined)
                    setCheckedSnap(undefined)
                    return
                  }

                  clearBootSourceIfIncompatible(count, false, 'subscription')
                }}
                onDurationChange={(nextDuration) => {
                  setDuration(nextDuration)
                  const totalDays = nextDuration === DURATION.DAILY ? 1 : 30
                  setPurchaseFor(totalDays)
                  setSubscriptionTotalDays(totalDays)

                  if (!supportsSubscription(machine, nextDuration)) {
                    setMachine(undefined)
                    setCheckedImage(undefined)
                    setCheckedSnap(undefined)
                  }
                }}
              />
              {isSubscription ? (
                <>
                  <div className="mt-4">
                    <SubscriptionCreditAlert />
                  </div>
                  {!rechargeCreditDataAvailable ? (
                    <div className="mt-4 rounded-lg bg-warning/10 p-3 t-sm text-warning">
                      <Trans i18nKey="create.subscriptionCreditBalanceUnavailable">
                        Unable to verify your Recharged Credits balance. Refresh the balance before
                        continuing.
                      </Trans>
                      <Button
                        type="link"
                        className="ml-2 p-0"
                        onClick={() => void userStore.getAccount()}
                      >
                        <Trans i18nKey="create.subscriptionRefreshBalance">Refresh balance</Trans>
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          <MachineSelector
            type={type}
            period={period}
            onTypeChange={setType}
            onChange={setMachine}
            count={count}
            checkedImage={checkedImage}
            setCheckedImage={setCheckedImage}
            checkedSnap={checkedSnap}
            setCheckedSnap={setCheckedSnap}
            enforceBootSourceResourceLimit
            supportsExtraResourcesForBootSource={!isSubscription}
            selectedMachine={machine}
            billingMethod={billingMethod}
            duration={duration}
          />
        </div>

        <div className="mt-8 px-8">
          <div className="t-sb mb-2 text-secondary" id="instance-quantity-label">
            <Trans i18nKey="create.leasedQuantity">Unit Qty</Trans>
          </div>

          {/* Unit Quantity */}
          <SelectMachineTooltip enabled={!machine}>
            <NumberInput
              data-ai-component="quantity-control"
              data-ai-action="set-instance-quantity"
              data-ai-state={machine ? 'enabled' : 'disabled'}
              data-ai-value={count}
              aria-labelledby="instance-quantity-label"
              value={count}
              onChange={(value) => {
                setCount(value)
                clearBootSourceIfIncompatible(value, !isSubscription, 'configuration')
              }}
              min={Math.max(machine?.unitStep ?? 1, minimumBootSourceInstanceCount)}
              max={machine?.maxAvailUnits ?? 1}
              step={machine?.unitStep ?? 1}
              className="w-[260px]"
              disabled={!machine}
            />
          </SelectMachineTooltip>
        </div>

        {!isSubscription ? (
          <ExtraSpecificationSection
            machine={machine}
            value={extraResourceConfig}
            minimumExtraResourceConfig={requiredExtraResourceConfig}
            minimumResource={getMinResource(selectedBootSource)}
            minimumDiskGB={requiredExtraStorageGB}
            minimumRequirementSource={checkedSnap ? 'snapshot' : 'image'}
            onChange={handleExtraResourceChange}
          />
        ) : null}

        <MountDriveSection
          machine={machine}
          mountVolume={mountVolume}
          mountPath={mountPath}
          readWriteMode={readWriteMode}
          volumes={cloudVolumes}
          loading={volumesLoading}
          config={{
            ...PERSONAL_SECTION_CONFIG,
            getVolumeQuota: (vol) => (userStore.isOrderExpired ? vol.last_quota_gb : vol.quota_gb),
          }}
          setMountModalOpen={setMountModalOpen}
          resetMountConfig={resetMountConfig}
          navigate={navigate}
        />

        {!isSubscription ? (
          <BindPublicIPSection
            machine={machine}
            boundNAT={boundNAT}
            setBindNATModalOpen={setBindNATModalOpen}
            setBoundNAT={setBoundNAT}
          />
        ) : null}

        {isSubscription ? (
          <>
            <PurchaseForSection
              duration={duration}
              purchaseFor={purchaseFor}
              subscriptionTotalDays={subscriptionTotalDays}
              onChange={({ purchaseFor: nextPurchaseFor, totalDays }) => {
                setPurchaseFor(nextPurchaseFor)
                setSubscriptionTotalDays(totalDays)
              }}
            />
            <div className="px-8">
              <AutoRenewalSection value={autoRenewal} onChange={setAutoRenewal} className="w-44" />
              <div className="mt-6">
                <div className="t-sb mb-2 text-secondary">
                  <Trans i18nKey="create.ExpirationBehaviorSection.expirationBehavior">
                    After Expiration
                  </Trans>
                </div>
                <ExpirationBehavior value={expirationBehavior} onChange={setExpirationBehavior} />
              </div>
            </div>
          </>
        ) : null}

        {isSubscription ? <div className="h-[92px]" aria-hidden="true" /> : null}

        <div className="absolute bottom-0 left-0 right-0 z-20 flex">
          <div className="w-full">
            <OrderSummary
              type={type}
              period={period}
              machine={machine}
              nat={isSubscription ? undefined : boundNAT}
              count={count}
              disabled={
                !canCreate ||
                resourceRequirementStatus.insufficient ||
                !subscriptionCheckoutDataAvailable
              }
              loading={priceLoading}
              priceAvailable={priceAvailable}
              extraStorageGB={extraStorageGB}
              extraCpuCores={extraResourceConfig.cpuCores}
              extraRamGB={extraResourceConfig.ramGB}
              requiredExtraStorageGB={minimumRequiredExtraStorageGB}
              availableExtraStorageGB={availableExtraStorageGB}
              storageInsufficient={storageInsufficient}
              priceMillicent={priceData?.discountPriceMillicent ?? priceData?.priceMillicent}
              basePriceMillicent={priceData?.basePriceMillicent}
              extraCpuPriceMillicent={extraCpuPriceMillicent}
              extraRamPriceMillicent={extraRamPriceMillicent}
              extraStoragePriceMillicent={extraStoragePriceMillicent}
              onConfirm={() => {
                if (storageInsufficient) {
                  setIsStorageWarningOpen(true)
                  return
                }
                if (
                  isSubscription &&
                  !hasSufficientRechargeCredits(subscriptionPriceCent, rechargeCreditCent)
                ) {
                  setInsufficientRechargeOpen(true)
                  return
                }
                setIsConfirmModalOpen(true)
              }}
              billingMethod={billingMethod}
              subscriptionTotalDays={isSubscription ? subscriptionTotalDays : undefined}
              autoRenewal={isSubscription ? autoRenewal : undefined}
              expirationBehavior={isSubscription ? expirationBehavior : undefined}
              duration={duration}
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={isConfirmModalOpen}
        isCreatingIns={isCreatingIns}
        machine={machine}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={onConfirm}
      />

      <InsufficientStorageModal
        open={isStorageWarningOpen}
        requiredStorageGB={selectedMinDiskGB}
        baseStorageGB={baseStorageGB}
        maxExtraStorageGB={availableExtraStorageGB}
        onClose={() => setIsStorageWarningOpen(false)}
      />

      {insufficientRechargeOpen ? (
        <InsufficientRechargeCreditsModal
          requiredCredits={String(formatPrice(subscriptionPriceCent ?? 0, false, ''))}
          availableCredits={String(formatPrice(rechargeCreditCent ?? 0, false, ''))}
          onClose={() => setInsufficientRechargeOpen(false)}
          onRecharge={() => {
            setInsufficientRechargeOpen(false)
            setExchargeModalOpen(true)
          }}
        />
      ) : null}

      {exchargeModalOpen ? (
        <ExchargeModal
          purpose="subscription"
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getAccount()
          }}
          onClose={() => setExchargeModalOpen(false)}
        />
      ) : null}

      {mountModalOpen &&
        (cloudVolumes.length > 0 ? (
          <MountDriveModal
            open={mountModalOpen}
            mountVolume={mountVolume}
            volumes={cloudVolumes}
            mountPath={mountPath}
            readWriteMode={readWriteMode}
            machineDomains={machine?.domains}
            canWriteVolume={!userStore.isOrderExpired && !hasPrivateRegionDomain(machine?.domains)}
            getVolumeQuota={(vol) => (userStore.isOrderExpired ? vol.last_quota_gb : vol.quota_gb)}
            onChange={(volume, path, mode) => {
              updateMountConfig(volume, path, mode)
              setMountModalOpen(false)
            }}
            onClose={() => setMountModalOpen(false)}
          />
        ) : (
          <Modal open={mountModalOpen} onClose={() => setMountModalOpen(false)}>
            <Modal.Header>Datadrive</Modal.Header>
            <Modal.Body>
              <InsufficientQuotaView />
            </Modal.Body>
          </Modal>
        ))}

      {!isSubscription && bindNATModalOpen && (
        <BindNATModal
          open={bindNATModalOpen}
          domain={machine?.domains?.[0]}
          boundNAT={boundNAT}
          onClose={() => setBindNATModalOpen(false)}
          onOk={(selectedIp) => {
            if (selectedIp) setBoundNAT(selectedIp)
            setBindNATModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
